import React from 'react';
import assign from 'lodash/assign';
import format from 'date-fns/format';
import { Link as v4Link } from 'react-router-dom';

// Constellation Components
import {
    Adornment,
    AdornedInput,
    Alert,
    Checkbox,
    FormField,
    FormHelp,
    Input,
    Label,
    LabeledControl,
    Popover,
    Select,
    Spacer,
    Text,
} from '@zillow/constellation';

import {
    Col,
    Link,
    Row,
} from '@zillow/zrs-comet';

import Constants from 'app/shared/constants';
import DateCalendar from 'app/shared/components/postingPath/DateCalendar';
import numberUtils from 'app/shared/utils/numberUtils';
import postingUtils from 'app/shared/utils/postingUtils';
import PropertyConstants from 'app/shared/constants/PropertyConstants';
import KeyCodeConstants from 'app/shared/constants/KeyCodeConstants';
import { RMWContext } from 'app/shared/providers/RMWContext';

import { StyledPopper, StyledTextButton, StyledZestimateFormHelp } from './StyledComponents';
import { StyledAnchor } from '../UtilsStyledComponents';
import '../utils.scss';

class ListingDetails extends React.Component {
    static contextType = RMWContext;

    constructor(props) {
        super(props);

        this.state = {
            formData: this.props.formData,
        };

        this.updateDescriptionOrLeaseTerms = this.updateDescriptionOrLeaseTerms.bind(this);
        this.updateFormData = this.updateFormData.bind(this);
        this.updateFloorplan = this.updateFloorplan.bind(this);
        this.hideAddressCheckboxHandler = this.hideAddressCheckboxHandler.bind(this);
        this.isValidPrice = this.isValidPrice.bind(this);
        this.roundNum = this.roundNum.bind(this);
        this.savePriceOrSecurityDeposit = this.savePriceOrSecurityDeposit.bind(this);
        this.updateSqFt = this.updateSqFt.bind(this);
        this.handleDateSelect = this.handleDateSelect.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        // shallow compare is ok as all properties of formData in this component are 1st level
        if (nextProps.formData !== this.state.formData) {
            this.setState({ formData: nextProps.formData });
        }
    }

    onKeyUp = (event) => {
        if (event.keyCode === KeyCodeConstants.ENTER) {
            event.target.click();
        }
    }

    updateDescriptionOrLeaseTerms(event) {
        const { target: { value } } = event;
        const propName = event.target.name;
        const { formData } = { ...this.state };
        const currentState = formData;
        currentState[propName] = value;
        this.setState({ formData: currentState });
    }

    updateFormData(event) {
        // Clear any associated errors with field when user enters input.
        const { target: { value } } = event;
        const propName = event.target.name;
        if ((propName === 'securityDeposit' || propName === 'price') &&
        value !== '' &&
        !numberUtils.isOnlyIntegers(value)) {
            return;
        }

        const { formData } = { ...this.state };
        const currentState = formData;
        currentState.floorplans[0][propName] = value;
        this.setState({ formData: currentState });
    }

    updateFloorplan(key, value, floorplanIndex) {
        const newFloorplans = [].concat(this.state.formData.floorplans);
        newFloorplans[floorplanIndex][key] = value;

        this.props.updateFormData({
            floorplans: newFloorplans,
        });
    }

    hideAddressCheckboxHandler() {
        this.props.immediateUpdateAndSave(
            'hideAddress', !this.state.formData.hideAddress
        );
    }

    isValidPrice(value) {
        let validNumber = true;
        const floorplanErrors = assign({}, this.props.errorObj.floorplans);

        if (isNaN(value)) {
            validNumber = false;
            floorplanErrors[0].price = 'Please enter only whole numbers.';
            this.props.updateErrorObj({ floorPlans: floorplanErrors });
        }

        return validNumber;
    }

    savePriceOrSecurityDeposit(value, field) {
        const floorplanErrors = assign({}, this.props.errorObj.floorplans);

        // if value is not empty and does not contains integer characters
        if (value !== '' && !numberUtils.isOnlyIntegers(value)) {
            floorplanErrors[0][field] = 'Please enter only whole numbers.';
            this.props.updateErrorObj({ floorPlans: floorplanErrors });
            return;
        }

        if (!this.isValidPrice(value)) {
            return;
        }

        const roundedPrice = this.roundNum(value);

        this.updateFloorplan(field, roundedPrice, 0);
        this.props.saveFloorplanData(field, roundedPrice);
    }

    // We can't pass a value of 0 to the API, which is what an empty field resolves to when using Math.round().
    // Check if value is 0 (which is invalid input anyway) and set the field to appear with an empty value.
    // This will prevent sending price=0 to the API and getting errors in the response.
    roundNum(value) {
        value = Math.round(value);
        if (Math.round(value) === 0) {
            value = '';
        }

        return value;
    }

    updateSqFt() {
        let { sqft } = this.state.formData.floorplans[0];
        const floorplanErrors = assign({}, this.props.errorObj.floorplans);

        sqft = postingUtils.validateSqFt(sqft);

        if (sqft === null) {
            floorplanErrors[0].sqft = 'Please enter only whole numbers.';
        } else {
            floorplanErrors[0].sqft = null;
            this.props.saveFloorplanData('sqft', sqft);
        }
        this.props.updateErrorObj({ floorPlans: floorplanErrors });
    }

    handleDateSelect(day, modifiers) {
        if (modifiers.disabled) {
            return;
        }

        const formattedDateForApi = format(new Date(day), 'YYYY-MM-DD');

        this.props.saveFloorplanData('dateAvailable', formattedDateForApi, 0);
    }

    render() {
        const {
            context,
            props,
            savePriceOrSecurityDeposit,
            updateFloorplan,
            updateSqFt,
        } = this;

        const {
            errorObj,
            saveFloorplanData,
            savePropertyData,
            updateFormData,
            updateOnFocus,
        } = props;

        const formData = this.state.formData;

        const {
            floorplans,
            hideAddress,
            leaseDuration,
            listingType,
            rentZestimate,
        } = formData;

        const {
            baths,
            beds,
            dateAvailable,
            price,
            securityDeposit,
            sqft,
        } = floorplans[0];

        const {
            floorplansRef,
            rentPriceRef,
            leaseDurationRef,
        } = this.props;

        const { paths, brand } = context;
        const floorplanErrorObj = errorObj.floorplans[0];
        const showZestimate = rentZestimate && brand === Constants.UNFORMATTED_BRAND_NAMES.zillow;
        const isDisabled = this.props.isInputFormDisabled;

        const isRoomForRent = listingType === PropertyConstants.LISTING_TYPES.ROOM;

        const bedOptions = ['', 'studio', '1', '2', '3', '4', '5', '6', '7', '8'];
        const bathOptions = ['', '1', '1.5', '2', '2.5', '3', '3.5', '4', '4.5', '5+'];
        if (beds !== '') {
            bedOptions.shift();
        }
        if (baths !== '') {
            bathOptions.shift();
        }
        const zestimatePopover = (
            <Popover
                body={(
                    <React.Fragment>
                        <Text fontType="body">
                            A Rent Zestimate® is Zillow’s estimated monthly rental price,
                            computed using a proprietary formula.
                            It is a starting point in determining the monthly rental price
                            for a specific property.
                        </Text>
                        <Text
                            marginTop="xs"
                            fontType="body"
                            as="div"
                        >
                            <Link
                                target="_blank"
                                reactRouterLink={v4Link}
                                to={paths.zestimatePage}
                            >
                                Learn more about Zestimates.
                            </Link>
                        </Text>
                    </React.Fragment>
                )}
            />
        );

        return (
            <div className="ListingDetails">
                <Row>
                    <Col xs={12} md={12} lg={6}>
                        <Text
                            fontType="h4"
                            as="div"
                            marginBottom="xs"
                            mediaQuery={{
                                lg: {
                                    marginBottom: 'sm',
                                },
                            }}
                        >
                            Listing information
                        </Text>

                        <StyledAnchor id="details" />
                        <div ref={rentPriceRef} />
                        <FormField marginBottom="sm">
                            <Label required={true}>Monthly rent</Label>
                            <AdornedInput
                                inputId="AdornedInput-Rent-Price"
                                leftAdornment={<Adornment>$</Adornment>}
                                rightAdornment={<Adornment>per month</Adornment>}
                                error={floorplanErrorObj.price}
                                disabled={isDisabled}
                                input={(
                                    <Input
                                        aria-label="Monthly rent"
                                        name="price"
                                        onBlur={e => savePriceOrSecurityDeposit(e.target.value, 'price')}
                                        onChange={this.updateFormData}
                                        onFocus={() => updateOnFocus(
                                            'price', price
                                        )}
                                        value={price}
                                        isRequired={true}
                                    />
                                )}
                            />
                            {floorplanErrorObj.price && (
                                <FormHelp error={true}>
                                    {floorplanErrorObj.price}
                                </FormHelp>
                            )}
                        </FormField>

                        {
                            showZestimate && (
                                <StyledZestimateFormHelp>
                                    {`The current Rent Zestimate: ${numberUtils.asCurrency(rentZestimate)}/mo `}
                                    <StyledPopper triggered={zestimatePopover} />
                                </StyledZestimateFormHelp>
                            )
                        }

                        <FormField>
                            <Label>Security deposit</Label>
                            <AdornedInput
                                inputId="AdornedInput-Security-Deposit"
                                leftAdornment={<Adornment>$</Adornment>}
                                error={floorplanErrorObj.securityDeposit}
                                disabled={isDisabled}
                                input={(
                                    <Input
                                        aria-label="Security deposit"
                                        name="securityDeposit"
                                        onBlur={e => savePriceOrSecurityDeposit(e.target.value, 'securityDeposit')}
                                        onChange={this.updateFormData}
                                        onFocus={() => updateOnFocus(
                                            'securityDeposit', securityDeposit
                                        )}
                                        value={securityDeposit}
                                    />
                                )}
                            />
                            {price && (
                                <StyledTextButton
                                    onClick={() => {
                                        savePriceOrSecurityDeposit(price, 'securityDeposit');
                                    }}
                                >
                                    Set to one month&#39;s rent
                                </StyledTextButton>
                            )}
                            <FormHelp>Amount generally should not exceed 150% of the base rent.</FormHelp>
                            {floorplanErrorObj.securityDeposit && (
                                <FormHelp error={true}>
                                    {floorplanErrorObj.securityDeposit}
                                </FormHelp>
                            )}
                        </FormField>
                        <Spacer marginBottom="sm" />
                    </Col>
                </Row>
                <Row>
                    <div ref={floorplansRef} />
                    <Col xs={12} md={4}>
                        <FormField marginBottom="sm">
                            <Label required={true}>Bedrooms</Label>
                            <Select
                                aria-label="Bedrooms"
                                error={floorplanErrorObj.beds}
                                name="beds"
                                onBlur={(e) => {
                                    e.stopPropagation();
                                    saveFloorplanData('beds', e.target.value);
                                }}
                                placeholder=""
                                type="text"
                                value={beds}
                                onChange={e => updateFloorplan('beds', e.target.value, 0)}
                                onFocus={() => updateOnFocus(
                                    'beds', beds
                                )}
                                disabled={isDisabled}
                                isRequired={true}
                            >
                                {bedOptions.map(((option) => {
                                    if (option === 'studio') {
                                        return <option key={option} value={0}>{option}</option>;
                                    }
                                    return <option key={option} value={option}>{option}</option>;
                                }))}
                            </Select>
                            {floorplanErrorObj.beds && (
                                <FormHelp error={true}>
                                    {floorplanErrorObj.beds}
                                </FormHelp>
                            )}
                        </FormField>
                    </Col>
                    <Col xs={12} md={4}>
                        <FormField marginBottom="sm">
                            <Label required={true}>Bathrooms</Label>
                            <Select
                                aria-label="Bathrooms"
                                error={floorplanErrorObj.baths}
                                name="baths"
                                min="0"
                                onBlur={(e) => {
                                    e.stopPropagation();
                                    saveFloorplanData('baths', e.target.value);
                                }}
                                placeholder="Baths"
                                type="number"
                                value={baths}
                                onChange={e => updateFloorplan('baths', e.target.value, 0)}
                                onFocus={() => updateOnFocus(
                                    'baths', baths
                                )}
                                disabled={isDisabled}
                                isRequired={true}
                            >
                                {bathOptions.map(((option) => {
                                    if (option === '5+') {
                                        return <option key={option} value={5}>{option}</option>;
                                    }
                                    return <option key={option} value={option}>{option}</option>;
                                }))}
                            </Select>
                            {floorplanErrorObj.baths && (
                                <FormHelp error={true}>
                                    {floorplanErrorObj.baths}
                                </FormHelp>
                            )}
                        </FormField>
                    </Col>
                    <Col xs={12} md={4}>
                        <FormField marginBottom="sm">
                            <Label>Square feet</Label>
                            <AdornedInput
                                inputId="AdornedInput-Square-Feet"
                                rightAdornment={<Adornment>sq. ft.</Adornment>}
                                error={floorplanErrorObj.securityDeposit}
                                disabled={isDisabled}
                                input={(
                                    <Input
                                        aria-label="Square feet"
                                        error={floorplanErrorObj.sqft}
                                        name="sqft"
                                        onBlur={e => updateSqFt(e.target.value)}
                                        value={sqft}
                                        onChange={this.updateFormData}
                                        onFocus={() => updateOnFocus(
                                            'sqft', sqft
                                        )}
                                    />
                                )}
                            />

                            {floorplanErrorObj.sqft && (
                                <FormHelp error={true}>
                                    {floorplanErrorObj.sqft}
                                </FormHelp>
                            )}
                        </FormField>
                    </Col>
                    {isRoomForRent && (
                        <Col marginBottom="sm">
                            <Text fontType="smallbody">
                                <strong>• Beds/baths</strong>
                                {' in the property. Your room for rent would be one of those.'}
                                <br />
                                <strong>• Square footage</strong>
                                {' of room being rented.'}
                            </Text>
                        </Col>
                    )}
                </Row>
                <Row>
                    <Col xs={12} md={12} lg={6}>
                        <DateCalendar
                            className="PostingPath-margin-bottom"
                            label="Date available"
                            dateAvailable={dateAvailable}
                            saveFloorplanData={saveFloorplanData}
                            isDisabled={isDisabled}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={8} lg={6}>
                        <div ref={leaseDurationRef} />
                        <FormField marginBottom="sm">
                            <Label required={true}>Lease duration</Label>
                            <Select
                                aria-label="Lease duration"
                                error={errorObj.leaseDuration}
                                name="leaseDuration"
                                onBlur={(e) => {
                                    e.stopPropagation();
                                    savePropertyData(
                                        'leaseDuration',
                                        e.target.value,
                                        { preserveFieldName: true }
                                    );
                                }}
                                placeholder="Select duration"
                                type="number"
                                value={this.state.formData.leaseDuration}
                                onChange={e => updateFormData({ leaseDuration: e.target.value })}
                                onFocus={() => updateOnFocus('leaseDuration', leaseDuration)}
                                disabled={isDisabled}
                                isRequired={true}
                            >
                                <option value="" defaultValue={true} disabled={true}>Select duration</option>
                                <option value="1mo">1 month</option>
                                <option value="6mo">6 months</option>
                                <option value="1y">1 year</option>
                                <option value="r2o">Rent to own</option>
                                <option value="sub">Sublet/temporary</option>
                            </Select>
                            {errorObj.leaseDuration && (
                                <FormHelp error={true}>
                                    {errorObj.leaseDuration}
                                </FormHelp>
                            )}
                        </FormField>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <LabeledControl
                            controlId="LabeledControl-hideAddress"
                            label={<Label>Hide property address on listing</Label>}
                            control={(
                                <Checkbox
                                    checked={hideAddress}
                                    onChange={this.hideAddressCheckboxHandler}
                                    disabled={isDisabled}
                                />
                            )}
                        />
                        {hideAddress && (
                            <Alert
                                appearance="warning"
                                // eslint-disable-next-line max-len
                                body="Listings with a hidden address may not appear on all sites and often receive fewer views and contacts than listings that display their address."
                            />
                        )}
                        <Spacer marginY="md" />
                    </Col>
                </Row>
            </div>
        );
    }
}

export default ListingDetails;
